import { BookmarkPlusIcon, BracesIcon, CalendarClockIcon, CheckCheckIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, ClockIcon, InfoIcon, LanguagesIcon, MessageSquareTextIcon, MoonIcon, SaveIcon, SendIcon, TriangleAlertIcon, UserRoundIcon, UsersIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { DayStamp, Incoming, PhoneFrame, SmsComposeBar, SmsHeader, SmsThread } from '@/components/brand/PhoneConversation';
import { OptionTiles } from '@/components/OptionTiles';
import { VariableTextarea } from '@/components/VariableTextarea';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { FormError } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useContactStats } from '@/features/contacts/queries';
import { usePreview, useSendMessage } from '@/features/history/queries';
import { useTemplateMutations } from '@/features/templates/queries';
import { TemplateEditorDialog } from '@/features/templates/TemplateEditorDialog';
import { TemplatePicker } from '@/features/templates/TemplatePicker';
import { useJobContext } from '@/layouts/JobLayout';
import { errorMessage, isApiError } from '@/lib/api';
import { useMe } from '@/lib/auth';
import { fmtDateTimeInZone, toDateTimeLocal, tzAbbreviation } from '@/lib/format';
import { useDebounce } from '@/lib/hooks';
import { BUILTIN_VARIABLES, CONTACT_VARIABLES, KIND_LABEL, builtinValues, contactExampleValues, extractTemplateVariables, renderTemplate, variableLabel, type TemplateVariable, type VariableInfo } from '@/lib/templates';
import type { Audience, ContactDto, PreviewResponse, SendOptions } from '@/lib/types';
import { cn } from '@/lib/utils';
import { AudiencePicker, type AudienceMode } from './AudiencePicker';

type When = 'now' | 'later' | 'draft';
type Sample = PreviewResponse['personalization']['samples'][number];

/**
 * Compose: write once, choose who gets it and when, and watch it land on a phone as you type.
 *
 * One composer card: the message with its toolbar underneath (start from a template, personalize, save as a
 * template, segment meter), then a "To" row and a "When" row that stay one line each until you pick something
 * that needs detail, and a languages line when translation is on. The recipient view (the text exactly as one
 * crew member reads it, in any language, for real recipients) stays beside it on desktop and under the editor
 * on a phone. A sticky action bar carries the live recipient count and the send button.
 */
export function ComposePage() {
  const { job, permissions } = useJobContext();
  const me = useMe();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const stats = useContactStats(job.id);
  const send = useSendMessage(job.id);
  const templateMutations = useTemplateMutations({ jobId: job.id });
  const features = me.tenant.features;

  const [body, setBody] = useState('');
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const [mode, setMode] = useState<AudienceMode>('all');
  const [tags, setTags] = useState<string[]>([]);
  const [selected, setSelected] = useState<Map<string, ContactDto>>(new Map());
  const [translate, setTranslate] = useState(features.translation && me.tenant.i18n.autoTranslate);
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [when, setWhen] = useState<When>('now');
  const [scheduledLocal, setScheduledLocal] = useState(() => toDateTimeLocal(new Date(Date.now() + 60 * 60_000)));
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [quietOverride, setQuietOverride] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lang, setLang] = useState<string>('source');
  const [sampleIdx, setSampleIdx] = useState(0);

  const debouncedBody = useDebounce(body, 300);
  const debouncedOverrides = useDebounce(overrides, 500);
  const audience: Audience | undefined = useMemo(() => {
    if (mode === 'all') return { type: 'all' };
    if (mode === 'tags') return tags.length ? { type: 'tags', tags } : undefined;
    return selected.size ? { type: 'selected', contactIds: [...selected.keys()] } : undefined;
  }, [mode, tags, selected]);

  const scheduledAt = when === 'later' && scheduledLocal ? new Date(scheduledLocal).toISOString() : null;
  const preview = usePreview(job.id, debouncedBody, audience, { translate, translationOverrides: debouncedOverrides, scheduledAt });
  const p = preview.data;
  const audienceIncomplete = audience === undefined;
  const tz = job.timezone ?? me.tenant.quietHours.timezone;
  const quietNow = !!p?.quietHours.activeNow;
  const eligible = p?.audience?.eligible ?? 0;

  // Variables: the local parse answers instantly while the server preview catches up with real recipients.
  const localVars = useMemo(() => extractTemplateVariables(body), [body]);
  const pz = p?.personalization;
  const fresh = !!pz && !preview.isPlaceholderData && debouncedBody === body;
  const unfilled = fresh ? pz.unfilled : localVars.filter((v) => v.kind === 'custom').map((v) => v.key);
  const personalized = localVars.some((v) => v.kind === 'contact');
  // Built-in fields the job has not filled in ({{job.address}} on a job with no address) would send as blank; say so.
  const emptyBuiltins = useMemo(() => {
    const vals = builtinValues(job, me);
    return localVars.filter((v) => v.kind === 'builtin' && !String(vals[v.key] ?? '').trim()).map((v) => v.key);
  }, [localVars, job, me]);
  const samples = pz?.samples ?? [];
  const sample = samples.length ? samples[sampleIdx % samples.length] : undefined;
  useEffect(() => setSampleIdx(0), [samples.length]);
  const values = useMemo(() => ({ ...builtinValues(job, me), ...(pz?.base ?? {}), ...(sample ? sample.values : contactExampleValues()) }), [job, me, pz?.base, sample]);

  const canSend = permissions.send && !!job.phoneNumber && body.trim().length > 0 && !!p && !p.overLimit && !audienceIncomplete && (when === 'draft' || (eligible > 0 && unfilled.length === 0));
  const blockedByQuiet = when === 'now' && quietNow && !quietOverride;
  const canSaveTemplate = features.templates && permissions.manageTemplates;

  const options: SendOptions = {
    translate,
    translationOverrides: Object.keys(overrides).length ? overrides : undefined,
    scheduledAt,
    saveAsDraft: when === 'draft',
    bypassQuietHours: when === 'now' ? quietOverride : undefined,
  };

  const doSend = async () => {
    if (!audience) return;
    setError(null);
    try {
      const res = await send.mutateAsync({ body: body.trim(), audience, options });
      const status = res.message.status;
      toast.success(status === 'draft' ? 'Draft saved' : status === 'scheduled' ? `Scheduled for ${fmtDateTimeInZone(res.message.scheduledAt, tz)}` : `Sending to ${res.recipients.eligible.toLocaleString()} recipients`);
      setConfirmOpen(false);
      navigate(status === 'queued' ? `/jobs/${job.id}/history/${res.message.id}` : `/jobs/${job.id}/history?view=upcoming`);
    } catch (err) {
      setConfirmOpen(false);
      if (isApiError(err) && err.code === 'quiet_hours') {
        setError(`${err.message} Tick "send anyway" under When to override, or schedule it.`);
        return;
      }
      setError(errorMessage(err));
    }
  };

  const insertTemplate = (text: string) => setBody((cur) => (cur.trim() ? `${cur.trimEnd()}\n\n${text}` : text));

  /** Drop a token at the caret (with a space before it when needed) and put the caret after it. */
  const insertToken = (token: string) => {
    const el = bodyRef.current;
    const cur = body;
    const start = el?.selectionStart ?? cur.length;
    const end = el?.selectionEnd ?? cur.length;
    const before = cur.slice(0, start);
    const pad = before && !/\s$/.test(before) ? ' ' : '';
    setBody(`${before}${pad}${token}${cur.slice(end)}`);
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const pos = start + pad.length + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const variant = lang === 'source' ? null : (p?.translations.find((t) => t.language === lang) ?? null);
  const templateParam = sp.get('template');
  const clearTemplateParam = () =>
    setSp(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('template');
        return next;
      },
      { replace: true },
    );

  const previewProps = { p, loading: preview.isPending, variant, lang, onLang: setLang, values, samples, sampleIdx, onSample: setSampleIdx };

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-6 xl:grid-cols-[minmax(0,1fr)_22rem] xl:items-start xl:gap-10 2xl:grid-cols-[minmax(0,1fr)_24rem]">
      <div className="grid min-w-0 gap-4">
        {!permissions.send && (
          <Alert variant="info">
            <InfoIcon />
            <AlertTitle>View only</AlertTitle>
            <AlertDescription>Only job admins can send messages. You can draft and preview here, but the send button is disabled for your role.</AlertDescription>
          </Alert>
        )}
        {!job.phoneNumber && (
          <Alert variant="destructive">
            <InfoIcon />
            <AlertTitle>No phone number</AlertTitle>
            <AlertDescription>This job cannot send until an organization admin assigns a phone number in Settings.</AlertDescription>
          </Alert>
        )}
        <FormError message={error} />

        <section className="@container bg-card min-w-0 overflow-x-clip border" aria-label="Compose">
          {/* ---------------------------------------------------------------- message */}
          <div className="px-4 pt-4 pb-4 sm:px-5">
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <label htmlFor="body" className="text-muted-foreground flex items-center gap-1.5 text-[11px] font-medium tracking-wide uppercase">
                <MessageSquareTextIcon className="size-3.5" aria-hidden /> Message
              </label>
              <span className="text-muted-foreground hidden text-xs sm:inline">
                {p?.prefix ? <>Starts with “{p.prefix.trim()}” · </> : null}Ends with “{p?.footer ?? 'Reply STOP to opt out.'}”
              </span>
            </div>
            {/* The field reads as the one thing to do on this page: a primary-tinted border until there is text, then it settles. */}
            <div className={cn('bg-background border transition-[border-color,box-shadow] focus-within:border-primary focus-within:ring-[3px] focus-within:ring-primary/15', body.trim() ? 'border-input' : 'border-primary/50')}>
              <VariableTextarea
                id="body"
                ref={bodyRef}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={6}
                placeholder="Write your message here. Say it the way you would at the gate: short, specific, one thing per text."
                className="min-h-[168px] resize-y rounded-none border-0 px-4 py-3 text-base leading-relaxed shadow-none focus-visible:ring-0 sm:min-h-[192px] sm:text-lg"
                maxLength={2000}
              />
              {localVars.length > 0 && <PersonalizationStrip vars={localVars} pz={pz} eligible={eligible} emptyKeys={emptyBuiltins} />}
            </div>
            <div className="bg-muted/30 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border border-t-0 px-1.5 py-1.5 sm:px-2">
              <div className="flex min-w-0 flex-wrap items-center gap-0.5">
                <TemplatePicker job={job} onInsert={insertTemplate} canManage={permissions.manageTemplates} autoPickId={templateParam} onAutoPicked={clearTemplateParam} />
                <VariableMenu onInsert={insertToken} />
                {canSaveTemplate && (
                  <Button type="button" variant="ghost" size="sm" disabled={!body.trim()} onClick={() => setSaveTemplateOpen(true)} aria-label="Save as template" title={body.trim() ? 'Save this text as a template for this job' : 'Write the message first'}>
                    <BookmarkPlusIcon /> <span className="hidden md:inline">Save as template</span>
                  </Button>
                )}
              </div>
              <SegmentMeter p={p} fallbackLength={body.length} longest={pz?.longest?.contactName ?? undefined} />
            </div>
          </div>


          {/* ---------------------------------------------------------------- to */}
          <ComposeRow
            icon={UsersIcon}
            label="To"
            aside={
              p?.audience && !audienceIncomplete ? (
                <span className="text-xs tabular-nums">
                  <span className="text-foreground font-medium">{eligible.toLocaleString()}</span> <span className="text-muted-foreground">will receive it{p.audience.optedOut > 0 || p.audience.invalid > 0 ? ` · ${[p.audience.optedOut > 0 ? `${p.audience.optedOut} opted out` : null, p.audience.invalid > 0 ? `${p.audience.invalid} invalid` : null].filter(Boolean).join(', ')} skipped` : ''}</span>
                </span>
              ) : (
                <span className="text-muted-foreground text-xs">{audienceIncomplete ? (mode === 'tags' ? 'Choose at least one tag' : 'Choose at least one person') : ''}</span>
              )
            }
          >
            <AudiencePicker jobId={job.id} mode={mode} onModeChange={setMode} tags={tags} onTagsChange={setTags} selected={selected} onSelectedChange={setSelected} totalContacts={stats.data?.total} />
          </ComposeRow>

          {/* ---------------------------------------------------------------- when */}
          <ComposeRow
            icon={ClockIcon}
            label="When"
            aside={
              quietNow ? (
                <Badge variant="warning" className="gap-1">
                  <MoonIcon className="size-3" /> Quiet hours at the site
                </Badge>
              ) : (
                <span className="text-muted-foreground text-xs tabular-nums">Site time {tzAbbreviation(tz)}</span>
              )
            }
          >
            <div className="grid gap-3">
              <OptionTiles
                ariaLabel="When to send"
                value={when}
                onChange={setWhen}
                options={[
                  { value: 'now', icon: SendIcon, title: 'Send now', hint: quietNow ? 'Quiet hours: needs an override' : 'Goes out immediately', warn: quietNow },
                  ...(features.scheduledSends ? [{ value: 'later' as When, icon: CalendarClockIcon, title: 'Schedule', hint: `Pick a time (${tzAbbreviation(tz)})` }] : []),
                  { value: 'draft', icon: SaveIcon, title: 'Save as draft', hint: 'Send later from History' },
                ]}
              />
              {when === 'later' && (
                <div className="grid gap-2 sm:grid-cols-[auto_1fr] sm:items-center">
                  <Input type="datetime-local" value={scheduledLocal} onChange={(e) => setScheduledLocal(e.target.value)} min={toDateTimeLocal(new Date())} className="w-full sm:w-auto" aria-label="Scheduled time" />
                  <span className="text-muted-foreground text-xs">
                    At the job site: <span className="text-foreground font-medium">{scheduledAt ? fmtDateTimeInZone(scheduledAt, tz) : '—'} {tzAbbreviation(tz)}</span>.
                    {p?.quietHours.scheduledInside && <span className="text-warning"> Falls inside quiet hours; it will move to {p.quietHours.window.end}.</span>}
                  </span>
                </div>
              )}
              {when === 'draft' && <p className="text-muted-foreground text-xs">Nothing is sent. Job admins can review and send it from History → Upcoming.</p>}
              {when === 'now' && quietNow && p && (
                <label className="border-warning/50 bg-warning/10 flex items-start gap-3 border p-3 text-sm">
                  <Checkbox checked={quietOverride} onCheckedChange={(v) => setQuietOverride(v === true)} className="mt-0.5" />
                  <span className="grid gap-0.5">
                    <span className="flex items-center gap-1.5 font-medium">
                      <MoonIcon className="size-3.5" /> Quiet hours at the site ({p.quietHours.window.start}–{p.quietHours.window.end} {tzAbbreviation(tz)}). Send anyway.
                    </span>
                    <span className="text-muted-foreground text-xs">Override only for urgent safety notices; otherwise schedule it for {p.quietHours.nextAllowedAt ? fmtDateTimeInZone(p.quietHours.nextAllowedAt, tz) : 'the morning'}.</span>
                  </span>
                </label>
              )}
            </div>
          </ComposeRow>

          {/* ---------------------------------------------------------------- languages */}
          {features.translation && <TranslationRow preview={p} translate={translate} onTranslateChange={setTranslate} overrides={overrides} onOverridesChange={setOverrides} loading={preview.isPending} />}

          {/* ---------------------------------------------------------------- the numbers behind the send */}
          <SendSummary p={p} from={job.phoneNumber?.display ?? null} tz={tz} />
        </section>

        {/* Compact recipient view for small screens; the phone lives in the right column on desktop. */}
        <div className="xl:hidden">
          <BubblePreview {...previewProps} />
        </div>
      </div>

      {/* Recipient view */}
      <aside className="hidden xl:sticky xl:top-[6.5rem] xl:block xl:self-start">
        <RecipientView {...previewProps} job={{ name: job.name, number: job.phoneNumber?.display ?? null }} when={when} scheduledAt={scheduledAt} tz={tz} />
      </aside>

      {/* Action bar: stays in reach while the page scrolls; sits above the mobile tab bar. */}
      <div className="bg-background/95 sticky bottom-[calc(4rem+env(safe-area-inset-bottom,0px))] z-20 -mx-4 border-t px-4 py-3 backdrop-blur md:bottom-0 md:-mx-8 md:px-8 xl:col-span-2">
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-3">
          <div className="text-muted-foreground flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            {p?.audience && !audienceIncomplete ? (
              <span className="flex items-center gap-1.5 tabular-nums">
                <CheckCheckIcon className="text-primary size-4" aria-hidden />
                <span className="text-foreground font-medium">{eligible.toLocaleString()}</span> {eligible === 1 ? 'recipient' : 'recipients'}
              </span>
            ) : (
              <span>Pick who gets it</span>
            )}
            {p && body.trim() && (
              <span className="hidden tabular-nums sm:inline">
                {p.segments.segments} {p.segments.segments === 1 ? 'segment' : 'segments'} each
                {p.translations.length > 0 ? ` · ${p.translations.length + 1} languages` : ''}
                {personalized ? ' · personalized' : ''}
              </span>
            )}
            {p?.overLimit && (
              <span className="text-destructive flex items-center gap-1">
                <TriangleAlertIcon className="size-3.5" /> Over the {p.maxLength} character limit
              </span>
            )}
            {unfilled.length > 0 && when !== 'draft' && body.trim() && (
              <span className="text-destructive flex items-center gap-1">
                <BracesIcon className="size-3.5" /> Fill in {unfilled.map((k) => `{{${k}}}`).join(', ')}
              </span>
            )}
            {blockedByQuiet && body.trim() && (
              <span className="text-warning flex items-center gap-1">
                <MoonIcon className="size-3.5" /> Quiet hours: tick “send anyway” or schedule
              </span>
            )}
            {emptyBuiltins.length > 0 && body.trim() && (
              <span className="text-warning flex items-center gap-1" title="These fields are blank on this job, so the text would go out with nothing in their place. Fill them in under Settings.">
                <TriangleAlertIcon className="size-3.5" /> {emptyBuiltins.map((k) => variableLabel(k)).join(', ')} {emptyBuiltins.length === 1 ? 'is' : 'are'} empty on this job
              </span>
            )}
          </div>
          {permissions.send ? (
            <Button size="lg" className="shrink-0 sm:min-w-[190px]" disabled={!canSend || send.isPending || blockedByQuiet} onClick={() => setConfirmOpen(true)}>
              {when === 'draft' ? (
                <>
                  <SaveIcon /> Save draft
                </>
              ) : when === 'later' ? (
                <>
                  <CalendarClockIcon /> <span className="hidden sm:inline">Review and schedule</span>
                  <span className="sm:hidden">Schedule</span>
                </>
              ) : (
                <>
                  <SendIcon /> <span className="hidden sm:inline">Review and send</span>
                  <span className="sm:hidden">Send</span>
                </>
              )}
            </Button>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0}>
                  <Button size="lg" disabled aria-disabled>
                    <SendIcon /> Send
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>Only job admins can send messages</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-normal tracking-tight">{when === 'draft' ? 'Save this draft?' : when === 'later' ? 'Schedule this message?' : `Send to ${eligible.toLocaleString()} ${eligible === 1 ? 'person' : 'people'}?`}</DialogTitle>
            <DialogDescription>
              {when === 'draft'
                ? 'Nothing is sent. Job admins can review and send it from History → Upcoming.'
                : when === 'later'
                  ? `Texts go out at ${scheduledAt ? fmtDateTimeInZone(scheduledAt, tz) : '—'} ${tzAbbreviation(tz)} from ${job.phoneNumber?.display ?? 'the job number'}. You can cancel until then.`
                  : `Texts go out immediately from ${job.phoneNumber?.display ?? 'the job number'} and cannot be recalled.`}
            </DialogDescription>
          </DialogHeader>
          {p && (
            <div className="grid gap-4 text-sm">
              <div className="bg-muted/40 border p-4">
                <Incoming>
                  <RenderedText p={p} variant={null} values={values} />
                </Incoming>
              </div>
              {personalized && (
                <p className="text-muted-foreground flex items-start gap-1.5 text-xs">
                  <UserRoundIcon className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                  <span>
                    Personalized for each recipient{sample ? `; shown for ${sample.name}` : ''}.{pz?.longest?.contactName ? ` The longest text is ${pz.longest.length} characters, for ${pz.longest.contactName}.` : ''}
                  </span>
                </p>
              )}
              {p.translations.length > 0 && (
                <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
                  <LanguagesIcon className="size-3.5" /> Also sent in {p.translations.map((t) => t.name).join(', ')} to contacts who prefer those languages.
                </p>
              )}
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 border-t pt-3 text-sm">
                <dt className="text-muted-foreground">Recipients</dt>
                <dd className="text-right tabular-nums">{p.audience?.eligible.toLocaleString() ?? '—'}</dd>
                <dt className="text-muted-foreground">Skipped, opted out</dt>
                <dd className="text-right tabular-nums">{p.audience?.optedOut ?? 0}</dd>
                <dt className="text-muted-foreground">Skipped, invalid number</dt>
                <dd className="text-right tabular-nums">{p.audience?.invalid ?? 0}</dd>
                <dt className="text-muted-foreground">Segments per message</dt>
                <dd className="text-right tabular-nums">{p.segments.segments}</dd>
              </dl>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={send.isPending}>
              Cancel
            </Button>
            <Button onClick={() => void doSend()} disabled={send.isPending}>
              {send.isPending ? 'Working…' : when === 'draft' ? 'Save draft' : when === 'later' ? 'Schedule' : `Send to ${eligible.toLocaleString()}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {canSaveTemplate && (
        <TemplateEditorDialog
          open={saveTemplateOpen}
          onOpenChange={setSaveTemplateOpen}
          template={null}
          initial={{ body }}
          scopeLabel={`Saved to ${job.name}; everyone on this job can start from it`}
          busy={templateMutations.create.isPending}
          onSave={(input) => templateMutations.create.mutateAsync(input)}
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------------------------------ pieces

/** A labelled row of the composer: icon + label on the left (above on phones), the control, and something useful on the right. */
function ComposeRow({ icon: Icon, label, aside, children }: { icon: typeof UsersIcon; label: string; aside?: ReactNode; children: ReactNode }) {
  return (
    <div className="grid gap-3 border-t px-4 py-4 @lg:grid-cols-[6.5rem_minmax(0,1fr)] sm:px-5">
      <div className="flex items-center justify-between gap-3 @lg:block">
        <span className="text-muted-foreground flex items-center gap-1.5 text-[11px] font-medium tracking-wide uppercase @lg:pt-2.5">
          <Icon className="size-3.5" aria-hidden /> {label}
        </span>
        <span className="@lg:hidden">{aside}</span>
      </div>
      <div className="grid gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0 flex-1">{children}</div>
        </div>
      </div>
      <div className="hidden @lg:col-start-2 @lg:-mt-2 @lg:block @lg:empty:hidden">{aside}</div>
    </div>
  );
}

/** Characters used against the segment size, with the segment count. Measured on the longest personalized text. */
function SegmentMeter({ p, fallbackLength, longest }: { p: PreviewResponse | undefined; fallbackLength: number; longest?: string }) {
  const len = p?.segments.length ?? fallbackLength;
  const per = p?.segments.perSegment ?? 160;
  const segments = p?.segments.segments ?? Math.max(1, Math.ceil(len / per));
  const capacity = per * Math.max(1, segments);
  const pct = Math.min(100, (len / capacity) * 100);
  const tone = p?.overLimit ? 'destructive' : segments > 1 ? 'warning' : 'ok';
  return (
    <div className="ml-auto flex shrink-0 items-center gap-2 pr-1 text-xs tabular-nums" title={`${len} of ${capacity} characters · ${segments} ${segments === 1 ? 'segment' : 'segments'}${p ? ` · ${p.segments.encoding}` : ''}${longest ? ` · longest for ${longest}` : ''}`}>
      <span className={cn('whitespace-nowrap', tone === 'destructive' ? 'text-destructive' : 'text-muted-foreground')}>
        <span className={tone === 'destructive' ? '' : 'text-foreground'}>{len}</span>/{capacity}
        <span className={cn('ml-1.5', tone === 'warning' && 'text-warning', tone === 'destructive' && 'text-destructive')}>
          · {segments} {segments === 1 ? 'segment' : 'segments'}
        </span>
      </span>
      <span className="bg-foreground/10 hidden h-1.5 w-14 overflow-hidden sm:block" aria-hidden>
        <span className={cn('block h-full transition-[width] duration-300', tone === 'destructive' ? 'bg-destructive' : tone === 'warning' ? 'bg-warning' : 'bg-primary')} style={{ width: `${pct}%` }} />
      </span>
    </div>
  );
}

/** "Personalize" menu: per-recipient fields first, then the job and sender details, each inserted at the caret. */
function VariableMenu({ onInsert }: { onInsert: (token: string) => void }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="ghost" size="sm">
          <BracesIcon /> Personalize
          <ChevronDownIcon className="hidden opacity-60 sm:block" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-80">
        <DropdownMenuLabel className="flex items-center gap-1.5">
          <UserRoundIcon className="size-3.5 opacity-60" aria-hidden /> For each recipient
        </DropdownMenuLabel>
        {CONTACT_VARIABLES.map((v) => (
          <VariableItem key={v.key} v={v} onInsert={onInsert} />
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>From the job and you</DropdownMenuLabel>
        {BUILTIN_VARIABLES.map((v) => (
          <VariableItem key={v.key} v={v} onInsert={onInsert} />
        ))}
        <DropdownMenuSeparator />
        <p className="text-muted-foreground px-2 py-1.5 text-xs leading-snug">
          Filled in as the text goes out, one message per person. Give empty fields a fallback: <code className="font-mono">{'{{contact.firstName|there}}'}</code>.
        </p>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function VariableItem({ v, onInsert }: { v: VariableInfo; onInsert: (token: string) => void }) {
  return (
    <DropdownMenuItem onClick={() => onInsert(`{{${v.key}}}`)} className="flex items-center justify-between gap-3">
      <span className="grid gap-0.5">
        <span className="font-medium">{v.label}</span>
        <span className="text-muted-foreground text-xs">e.g. {v.example}</span>
      </span>
      <code className="text-muted-foreground font-mono text-[11px]">{`{{${v.key}}}`}</code>
    </DropdownMenuItem>
  );
}

/** What the variables in the text will do, with a nudge for blanks nothing fills and for recipients missing a field. */
function PersonalizationStrip({ vars, pz, eligible, emptyKeys }: { vars: TemplateVariable[]; pz: PreviewResponse['personalization'] | undefined; eligible: number; emptyKeys: string[] }) {
  const contact = vars.filter((v) => v.kind === 'contact');
  const builtin = vars.filter((v) => v.kind === 'builtin');
  const custom = vars.filter((v) => v.kind === 'custom');
  return (
    <div className="px-4 pb-2.5 text-xs">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-muted-foreground flex items-center gap-1 font-medium">
          {contact.length > 0 ? <UserRoundIcon className="size-3.5" aria-hidden /> : <BracesIcon className="size-3.5" aria-hidden />}
          {contact.length > 0 ? 'Personalized for each recipient' : 'Variables'}
        </span>
        {contact.map((v) => {
          const miss = pz?.missing[v.key] ?? 0;
          const title = miss > 0 ? `${miss} of ${eligible} have no ${variableLabel(v.key).toLowerCase()}${v.fallback ? ` and get “${v.fallback}”` : '; they get a blank. Add a fallback: {{' + v.key + '|there}}'}` : KIND_LABEL.contact;
          return (
            <Chip key={v.key} tone="contact" title={title}>
              {variableLabel(v.key)}
              {v.fallback ? <span className="opacity-70">| {v.fallback}</span> : null}
              {miss > 0 && !v.fallback ? <span className="text-warning"> · {miss} blank</span> : null}
            </Chip>
          );
        })}
        {builtin.map((v) => {
          const empty = emptyKeys.includes(v.key);
          return (
            <Chip key={v.key} tone={empty ? 'empty' : 'builtin'} title={empty ? `${variableLabel(v.key)} is not set for this job, so this would send as blank. Fill it in under Settings.` : KIND_LABEL.builtin}>
              {variableLabel(v.key)}
              {empty ? <span className="text-warning"> · empty</span> : null}
            </Chip>
          );
        })}
        {custom.map((v) => (
          <Chip key={v.key} tone="custom" title="Nothing fills this in. Replace it with text, or with a field from Personalize.">
            {`{{${v.key}}}`} needs a value
          </Chip>
        ))}
      </div>
    </div>
  );
}

function Chip({ tone, title, children }: { tone: 'contact' | 'builtin' | 'custom' | 'empty'; title?: string; children: ReactNode }) {
  const cls = tone === 'contact' ? 'border-primary/40 bg-accent text-accent-foreground' : tone === 'custom' ? 'border-destructive/40 bg-destructive/10 text-destructive' : tone === 'empty' ? 'border-warning/60 bg-warning/15 text-foreground' : 'bg-muted text-muted-foreground';
  return (
    <span className={cn('inline-flex items-center gap-1 border px-1.5 py-0.5 font-mono text-[11px]', cls)} title={title}>
      {children}
    </span>
  );
}

/** Flip the preview between a few real recipients. */
function SampleSwitcher({ samples, idx, onIdx }: { samples: Sample[]; idx: number; onIdx: (i: number) => void }) {
  if (samples.length === 0) return null;
  const n = samples.length;
  const s = samples[idx % n]!;
  return (
    <span className="flex items-center gap-1 text-xs">
      <button type="button" className="text-muted-foreground hover:text-foreground grid size-6 place-items-center disabled:opacity-40" onClick={() => onIdx((idx - 1 + n) % n)} disabled={n < 2} aria-label="Previous recipient">
        <ChevronLeftIcon className="size-3.5" />
      </button>
      <span className="flex items-center gap-1 tabular-nums" title="Preview as this recipient">
        <UserRoundIcon className="size-3.5 opacity-60" aria-hidden />
        <span className="max-w-[140px] truncate">{s.name}</span>
        {n > 1 && (
          <span className="text-muted-foreground">
            {(idx % n) + 1}/{n}
          </span>
        )}
      </span>
      <button type="button" className="text-muted-foreground hover:text-foreground grid size-6 place-items-center disabled:opacity-40" onClick={() => onIdx((idx + 1) % n)} disabled={n < 2} aria-label="Next recipient">
        <ChevronRightIcon className="size-3.5" />
      </button>
    </span>
  );
}

/**
 * Languages as one line: who speaks what, and the translate switch. Machine translations are there to review
 * and edit, but folded away until asked for.
 */
function TranslationRow({ preview, translate, onTranslateChange, overrides, onOverridesChange, loading }: { preview: PreviewResponse | undefined; translate: boolean; onTranslateChange: (v: boolean) => void; overrides: Record<string, string>; onOverridesChange: (o: Record<string, string>) => void; loading: boolean }) {
  const languages = preview?.audience?.languages ?? [];
  const others = languages.filter((l) => l.language !== preview?.sourceLanguage);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const translations = preview?.translations ?? [];
  return (
    <div className="grid gap-3 border-t px-4 py-4 @lg:grid-cols-[6.5rem_minmax(0,1fr)] sm:px-5">
      <span className="text-muted-foreground flex items-center gap-1.5 text-[11px] font-medium tracking-wide uppercase @lg:pt-1">
        <LanguagesIcon className="size-3.5" aria-hidden /> Languages
      </span>
      <div className="grid gap-3">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="flex min-w-0 flex-wrap items-center gap-1.5 text-xs">
            {languages.length === 0 ? (
              <span className="text-muted-foreground">Pick an audience to see who speaks what.</span>
            ) : (
              languages.map((l) => (
                <span key={l.language} className={cn('border px-2 py-0.5 tabular-nums', l.language === preview?.sourceLanguage ? 'bg-muted' : 'bg-card')}>
                  {l.name} <span className="text-muted-foreground">{l.count}</span>
                </span>
              ))
            )}
            {preview && !preview.translationAvailable && <span className="text-muted-foreground">Translation is not available on this environment.</span>}
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={translate} onCheckedChange={onTranslateChange} disabled={preview ? !preview.translationAvailable : false} /> Translate automatically
          </label>
        </div>
        {translate && others.length > 0 && (
          <div className="grid gap-2">
            <button type="button" onClick={() => setOpen((o) => !o)} className="text-muted-foreground hover:text-foreground flex w-fit items-center gap-1 text-xs underline-offset-4 hover:underline" aria-expanded={open}>
              <ChevronDownIcon className={cn('size-3.5 transition-transform', open && 'rotate-180')} aria-hidden />
              {open ? 'Hide translations' : `Review ${translations.length ? `${translations.length} ${translations.length === 1 ? 'translation' : 'translations'}` : 'translations'}`}
              {translations.some((t) => t.overridden) && <span className="text-foreground">· edited</span>}
            </button>
            {open && (
              <div className="grid gap-2">
                {loading && translations.length === 0 && <Skeleton className="h-16 w-full" />}
                {translations.map((t) => (
                  <div key={t.language} className="bg-muted/30 grid gap-1.5 border p-3">
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span className="font-medium">{t.name}</span>
                      <span className="text-muted-foreground tabular-nums">
                        {languages.find((l) => l.language === t.language)?.count ?? 0} {(languages.find((l) => l.language === t.language)?.count ?? 0) === 1 ? 'recipient' : 'recipients'} · {t.segments.segments} {t.segments.segments === 1 ? 'segment' : 'segments'}
                      </span>
                      <Badge variant={t.overridden ? 'secondary' : 'muted'}>{t.overridden ? 'Edited by you' : `Machine translation${t.provider === 'mock' ? ' (simulated)' : ''}`}</Badge>
                      <Button variant="ghost" size="xs" className="ml-auto" onClick={() => setEditing(editing === t.language ? null : t.language)}>
                        {editing === t.language ? 'Done' : 'Edit'}
                      </Button>
                      {t.overridden && (
                        <Button
                          variant="ghost"
                          size="xs"
                          onClick={() => {
                            const next = { ...overrides };
                            delete next[t.language];
                            onOverridesChange(next);
                          }}
                        >
                          Reset
                        </Button>
                      )}
                    </div>
                    {editing === t.language ? <Textarea rows={3} value={overrides[t.language] ?? t.body} onChange={(e) => onOverridesChange({ ...overrides, [t.language]: e.target.value })} className="bg-card text-sm" /> : <p className="text-sm whitespace-pre-wrap">{t.rendered}</p>}
                  </div>
                ))}
                {others.filter((l) => !translations.some((t) => t.language === l.language)).length > 0 && !loading && <p className="text-muted-foreground text-xs">Some languages could not be translated right now; those contacts receive the original text.</p>}
              </div>
            )}
          </div>
        )}
        {translate && languages.length > 0 && others.length === 0 && <p className="text-muted-foreground text-xs">Everyone in this audience speaks the source language; nothing to translate.</p>}
      </div>
    </div>
  );
}

/** The text as it lands for one recipient: bold sender label, the body with variables filled, the opt-out line dimmed; or a translated variant. */
function RenderedText({ p, variant, values }: { p: PreviewResponse | undefined; variant: PreviewResponse['translations'][number] | null; values: Record<string, string> }) {
  if (!p) return <span className="text-muted-foreground italic">Your message appears here as the crew will see it.</span>;
  if (variant) return <>{renderTemplate(variant.rendered, values)}</>;
  const body = renderTemplate(p.body, values);
  return (
    <>
      {p.prefix && <span className="font-semibold">{p.prefix}</span>}
      {body ? body : <span className="text-muted-foreground italic">Your message appears here as the crew will see it.</span>}
      {p.footer && (
        <>
          {body ? '\n\n' : ' '}
          <span className="opacity-70">{p.footer}</span>
        </>
      )}
    </>
  );
}

function LanguagePills({ p, lang, onLang }: { p: PreviewResponse | undefined; lang: string; onLang: (l: string) => void }) {
  if (!p || p.translations.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1" role="tablist" aria-label="Preview language">
      {[{ language: 'source', name: 'Original' }, ...p.translations].map((t) => (
        <button key={t.language} type="button" role="tab" aria-selected={lang === t.language} onClick={() => onLang(t.language)} className={cn('border px-2.5 py-1 text-xs transition-colors', lang === t.language ? 'border-primary bg-primary text-primary-foreground' : 'bg-card hover:border-foreground/30')}>
          {t.name}
        </button>
      ))}
    </div>
  );
}

function previewLabel(sample: Sample | undefined): string {
  if (!sample) return 'On their phone';
  const first = sample.name.split(/\s+/)[0] ?? sample.name;
  return `On ${first}${/s$/i.test(first) ? '’' : '’s'} phone`;
}

interface PreviewProps {
  p: PreviewResponse | undefined;
  loading: boolean;
  variant: PreviewResponse['translations'][number] | null;
  lang: string;
  onLang: (l: string) => void;
  values: Record<string, string>;
  samples: Sample[];
  sampleIdx: number;
  onSample: (i: number) => void;
}

/** Small-screen preview: just the bubble, no device. */
function BubblePreview({ p, loading, variant, lang, onLang, values, samples, sampleIdx, onSample }: PreviewProps) {
  const sample = samples.length ? samples[sampleIdx % samples.length] : undefined;
  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">{previewLabel(sample)}</span>
        <div className="flex flex-wrap items-center gap-3">
          <SampleSwitcher samples={samples} idx={sampleIdx} onIdx={onSample} />
          <LanguagePills p={p} lang={lang} onLang={onLang} />
        </div>
      </div>
      <div className="bg-muted/40 border p-4">
        {loading && !p ? (
          <Skeleton className="h-16 w-4/5" />
        ) : (
          <Incoming>
            <RenderedText p={p} variant={variant} values={values} />
          </Incoming>
        )}
      </div>
    </div>
  );
}

/** Desktop preview: the message on a worker's phone, drawn larger than the demo's device. The facts behind the send sit under the composer. */
function RecipientView({ p, loading, job, variant, lang, onLang, when, scheduledAt, tz, values, samples, sampleIdx, onSample }: PreviewProps & { job: { name: string; number: string | null }; when: When; scheduledAt: string | null; tz: string }) {
  const clock = useMemo(() => new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(new Date()).replace(/\s?[AP]M$/i, ''), []);
  const stamp = when === 'later' && scheduledAt ? fmtDateTimeInZone(scheduledAt, tz) : 'Today · now';
  const sample = samples.length ? samples[sampleIdx % samples.length] : undefined;
  return (
    <div className="grid gap-4" aria-label="Recipient view">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">{previewLabel(sample)}</span>
        <div className="flex flex-wrap items-center gap-3">
          <SampleSwitcher samples={samples} idx={sampleIdx} onIdx={onSample} />
          <LanguagePills p={p} lang={lang} onLang={onLang} />
        </div>
      </div>
      <div className="flex justify-center" aria-hidden>
        <div className="h-[650px] w-[320px] 2xl:h-[702px] 2xl:w-[346px]">
          <PhoneFrame time={clock} className="origin-top-left scale-[1.25] 2xl:scale-[1.35]">
            <SmsHeader name={job.name} number={job.number ?? 'No number yet'} />
            <SmsThread>
              <DayStamp>{stamp}</DayStamp>
              {loading && !p ? (
                <div className="pr-7">
                  <Skeleton className="h-16 w-full rounded-[16px]" />
                </div>
              ) : (
                <Incoming>
                  <RenderedText p={p} variant={variant} values={values} />
                </Incoming>
              )}
            </SmsThread>
            <SmsComposeBar />
          </PhoneFrame>
        </div>
      </div>

    </div>
  );
}

/** The numbers behind the send: who it comes from, who gets it, what it costs, what could stop it. Under the composer on every screen size. */
function SendSummary({ p, from, tz }: { p: PreviewResponse | undefined; from: string | null; tz: string }) {
  const eligible = p?.audience?.eligible ?? null;
  const segmentsTotal = p?.audience && p.audience.eligible > 0 ? p.audience.eligible * p.segments.segments : null;
  const skipped = p?.audience ? [p.audience.optedOut > 0 ? `${p.audience.optedOut} opted out` : null, p.audience.invalid > 0 ? `${p.audience.invalid} invalid` : null].filter(Boolean).join(' · ') : '';
  const quiet = p?.quietHours;
  return (
    <div className="bg-muted/30 border-t px-4 py-3.5 sm:px-5">
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 @md:grid-cols-4">
        <Fact label="From">{from ?? p?.fromNumber ?? <span className="text-destructive">No number assigned</span>}</Fact>
        <Fact label="Recipients" sub={skipped ? `${skipped} skipped` : undefined}>
          {eligible !== null ? eligible.toLocaleString() : '—'}
        </Fact>
        <Fact label="Segments" sub={segmentsTotal ? `${segmentsTotal.toLocaleString()} total` : undefined}>
          {p ? `${p.segments.segments} per message` : '—'}
        </Fact>
        <Fact label="Quiet hours" sub={quiet?.activeNow ? 'Active now' : undefined} tone={quiet?.activeNow ? 'warning' : undefined}>
          {quiet ? (quiet.window.enabled ? `${quiet.window.start}–${quiet.window.end} ${tzAbbreviation(tz)}` : 'Off for this job') : '—'}
        </Fact>
        {p?.personalization.longest?.contactName && (
          <Fact label="Longest text" sub={p.personalization.longest.contactName}>
            {p.personalization.longest.length} chars
          </Fact>
        )}
        {p?.audience && p.audience.languages.length > 1 && <Fact label="Languages">{p.audience.languages.map((l) => `${l.name} ${l.count}`).join(' · ')}</Fact>}
      </dl>
      {p && (p.overLimit || p.warnings.length > 0) && (
        <ul className="mt-3 grid gap-1 text-xs">
          {p.overLimit && (
            <li className="text-destructive flex items-start gap-1.5">
              <TriangleAlertIcon className="mt-0.5 size-3.5 shrink-0" /> Over the {p.maxLength} character limit including the opt-out line.
            </li>
          )}
          {p.warnings.map((w) => (
            <li key={w} className="text-warning flex items-start gap-1.5">
              <TriangleAlertIcon className="mt-0.5 size-3.5 shrink-0" /> {w}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Fact({ label, sub, tone, children }: { label: string; sub?: string; tone?: 'warning'; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">{label}</dt>
      <dd className={cn('mt-0.5 text-sm leading-snug tabular-nums', tone === 'warning' && 'text-warning')}>{children}</dd>
      {sub && <dd className="text-muted-foreground text-xs">{sub}</dd>}
    </div>
  );
}
